import { useState } from "react";
import { ChevronLeft, ChevronRight, PlayCircle } from "lucide-react";
import { extractYoutubeId, buildYoutubeEmbedUrl } from "@/lib/youtube";

// Normalizes a raw `model.gallery` entry (string URL, or {src, alt, type, poster, pos}
// object) into a consistent shape. Shared by every product detail page so image vs.
// video vs. positioned-image handling only needs to be written once.
export const normalizeGalleryImage = (g) =>
  typeof g === "string"
    ? { src: g, alt: null, type: "image", pos: "center", caption: null }
    : { src: g.src, alt: g.alt || null, type: g.type || "image", poster: g.poster || null, pos: g.pos || "center", caption: g.caption || null };

// Full-size image/video viewer + thumbnail strip, used on every product detail page.
// Owns its own `activeImg` state — pass `productName` for alt-text fallbacks.
// Optional styling overrides (defaults match the original dark/cover look):
// - bgClassName: main viewer + thumbnail background (e.g. "bg-white" for white-background product shots)
// - imageFit / imagePadding: main image object-fit + padding (e.g. "object-contain" + "p-6")
// - thumbFit / thumbPadding: thumbnail image object-fit + padding
// - thumbGridClassName: thumbnail grid column classes (e.g. "grid-cols-5 md:grid-cols-6")
export const ProductGallery = ({
  gallery,
  productName,
  bgClassName = "bg-[var(--bg-secondary)]",
  imageFit = "object-cover",
  imagePadding = "",
  thumbFit = "object-cover",
  thumbPadding = "",
  thumbGridClassName = "grid-cols-6",
  initialThumbCount = null,
}) => {
  const [activeImg, setActiveImg] = useState(0);
  const [touchStartX, setTouchStartX] = useState(null);
  const [showAllThumbs, setShowAllThumbs] = useState(!initialThumbCount);
  const activeSlide = gallery[activeImg];
  const isVideoSlide = activeSlide?.type === "video";
  const activeYoutubeId = isVideoSlide ? extractYoutubeId(activeSlide.src) : null;

  const goToPrev = () => setActiveImg((activeImg - 1 + gallery.length) % gallery.length);
  const goToNext = () => setActiveImg((activeImg + 1) % gallery.length);

  const handleTouchStart = (e) => setTouchStartX(e.touches[0].clientX);
  const handleTouchEnd = (e) => {
    if (touchStartX === null || gallery.length <= 1) return;
    const deltaX = e.changedTouches[0].clientX - touchStartX;
    const SWIPE_THRESHOLD = 50;
    if (deltaX > SWIPE_THRESHOLD) goToPrev();
    else if (deltaX < -SWIPE_THRESHOLD) goToNext();
    setTouchStartX(null);
  };

  return (
    <div>
      <div
        className={`relative aspect-[4/3] overflow-hidden border border-[var(--border-default)] ${bgClassName}`}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {isVideoSlide ? (
          activeYoutubeId ? (
            <iframe
              key={activeImg}
              data-testid="gallery-video"
              src={buildYoutubeEmbedUrl(activeYoutubeId)}
              title={activeSlide.alt || `${productName} video`}
              className="w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <video key={activeImg} data-testid="gallery-video" controls autoPlay muted playsInline poster={activeSlide.poster} className="w-full h-full object-cover">
              <source src={activeSlide.src} type="video/mp4" />
            </video>
          )
        ) : (
          <img
            key={activeImg}
            src={activeSlide.src}
            alt={activeSlide.alt || `${productName} view ${activeImg + 1}`}
            className={`w-full h-full ${imageFit} ${imagePadding}`}
            style={{ objectPosition: activeSlide.pos }}
          />
        )}
        {!isVideoSlide && activeSlide.caption && (
          <span
            data-testid="gallery-caption"
            className="absolute bottom-12 sm:bottom-3 right-3 px-3 py-1.5 bg-black/55 backdrop-blur-sm text-white/90 text-sm font-mono uppercase tracking-wide"
          >
            {activeSlide.caption}
          </span>
        )}
        {gallery.length > 1 && (
          <>
            <button
              onClick={goToPrev}
              data-testid="gallery-prev"
              className="hidden sm:flex absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 bg-black/60 backdrop-blur-sm border border-white/10 text-white hover:bg-[var(--brand-primary)] hover:text-black transition-colors items-center justify-center"
              aria-label="Previous image"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={goToNext}
              data-testid="gallery-next"
              className="hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 bg-black/60 backdrop-blur-sm border border-white/10 text-white hover:bg-[var(--brand-primary)] hover:text-black transition-colors items-center justify-center"
              aria-label="Next image"
            >
              <ChevronRight size={20} />
            </button>
            <span
              data-testid="gallery-mobile-counter"
              className="sm:hidden absolute bottom-3 right-3 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur-sm text-white text-xs font-mono tracking-wide"
            >
              {activeImg + 1} / {gallery.length}
            </span>
          </>
        )}
      </div>
      {gallery.length > 1 && (
        <div className={`grid ${thumbGridClassName} gap-2 mt-3`}>
          {(showAllThumbs ? gallery : gallery.slice(0, initialThumbCount)).map((img, i) => {
            const isVideo = img.type === "video";
            return (
              <button
                key={img.src || i}
                onClick={() => setActiveImg(i)}
                data-testid={`gallery-thumb-${i}`}
                className={`relative aspect-square overflow-hidden border transition-colors ${bgClassName} ${i === activeImg ? "border-[var(--brand-primary)]" : "border-[var(--border-default)] hover:border-white/40"}`}
              >
                <img
                  src={isVideo ? img.poster : img.src}
                  alt={isVideo ? "Product video" : (img.alt || `Thumbnail ${i + 1}`)}
                  className={`w-full h-full ${thumbFit} ${thumbPadding}`}
                  style={{ objectPosition: isVideo ? "center" : img.pos }}
                  loading="lazy"
                />
                {isVideo && (
                  <span className="absolute inset-0 flex items-center justify-center bg-black/40">
                    <PlayCircle size={18} className="text-white" strokeWidth={1.5} />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
      {!showAllThumbs && initialThumbCount && gallery.length > initialThumbCount && (
        <button
          onClick={() => setShowAllThumbs(true)}
          data-testid="gallery-view-all"
          className="mt-3 w-full py-3 border border-[var(--border-default)] text-sm font-mono uppercase tracking-wide text-white/80 hover:text-white hover:border-white/40 transition-colors"
        >
          View All {gallery.length} Photos
        </button>
      )}
    </div>
  );
};
